import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { Project, Component, ProjectWithComponents } from '../types';
import { apiClient } from '../services/apiClient';

interface GlobalData {
  projects: ProjectWithComponents[]; // Projects with their nested components
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

interface DataContextType {
  data: GlobalData;
  refreshData: () => Promise<void>;
  getProject: (projectId: string) => ProjectWithComponents | undefined;
  getProjectByName: (projectName: string) => ProjectWithComponents | undefined;
  getProjectComponents: (projectId: string) => Component[];
  getProjectComponentsByName: (projectName: string) => Component[];
  getAllProjects: () => ProjectWithComponents[];
  getAllComponents: () => Component[];
  isLoading: boolean;
  error: string | null;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [data, setData] = useState<GlobalData>({
    projects: [],
    loading: true,
    error: null,
    lastUpdated: null
  });

  const fetchAllData = useCallback(async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      console.log('DataContext: Starting to fetch data from API');

      // Fetch all projects using the API client
      const apiProjects = await apiClient.getProjects();
      console.log(`DataContext: Fetched ${apiProjects.length} projects from API`);
      console.log('DataContext: API Projects structure:', apiProjects.map(p => ({
        id: p.id,
        name: p.name,
        componentsCount: p.componentsCount,
        status: p.status
      })));
      
      // Use API projects directly - they already match our Project interface
      const projects = apiProjects;
      console.log('DataContext: Using API projects directly');
      
      // Create projects with nested components
      const projectsWithComponents: ProjectWithComponents[] = [];
      
      console.log('DataContext: Starting to fetch components for', projects.length, 'projects');
      
      for (const project of projects) {
        try {
          console.log(`DataContext: Fetching components for project ${project.name} (ID: ${project.id})`);
          const apiProjectComponents = await apiClient.getProjectComponents(project.id);
          console.log(`DataContext: Received ${apiProjectComponents.length} components for project ${project.name}`);
          console.log(`DataContext: Components structure for ${project.name}:`, apiProjectComponents.slice(0, 2).map(c => ({
            name: c.name,
            type: c.type,
            status: c.status
          })));
          
          // Use API components directly - they already match our Component interface
          const projectComponents = apiProjectComponents;
          console.log(`DataContext: Using ${projectComponents.length} API components directly for project ${project.name}`);
          
          // Create project with nested components
          const projectWithComponents: ProjectWithComponents = {
            ...project,
            components: projectComponents
          };
          
          projectsWithComponents.push(projectWithComponents);
        } catch (error) {
          console.error(`Error fetching components for project ${project.name}:`, error);
          // Add project with empty components array
          const projectWithComponents: ProjectWithComponents = {
            ...project,
            components: []
          };
          projectsWithComponents.push(projectWithComponents);
        }
      }
      
      console.log('DataContext: Final data structure:', projectsWithComponents);
      console.log('DataContext: Total components across all projects:', projectsWithComponents.reduce((sum, p) => sum + p.components.length, 0));

      setData({
        projects: projectsWithComponents,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error fetching global data:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch data'
      }));
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Helper functions - memoized to prevent recreation on every render
  const getProject = useCallback((projectId: string): ProjectWithComponents | undefined => {
    return data.projects.find(p => p.id === projectId);
  }, [data.projects]);

  const getProjectByName = useCallback((projectName: string): ProjectWithComponents | undefined => {
    return data.projects.find(p => p.name === projectName);
  }, [data.projects]);

  const getProjectComponents = useCallback((projectId: string): Component[] => {
    const project = data.projects.find(p => p.id === projectId);
    return project ? project.components : [];
  }, [data.projects]);

  const getProjectComponentsByName = useCallback((projectName: string): Component[] => {
    const project = data.projects.find(p => p.name === projectName);
    return project ? project.components : [];
  }, [data.projects]);

  const getAllProjects = useCallback((): ProjectWithComponents[] => {
    return data.projects;
  }, [data.projects]);

  const getAllComponents = useCallback((): Component[] => {
    return data.projects.flatMap(p => p.components);
  }, [data.projects]);

  const refreshData = useCallback(async () => {
    await fetchAllData();
  }, [fetchAllData]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue: DataContextType = useMemo(() => ({
    data,
    refreshData,
    getProject,
    getProjectByName,
    getProjectComponents,
    getProjectComponentsByName,
    getAllProjects,
    getAllComponents,
    isLoading: data.loading,
    error: data.error
  }), [data, refreshData, getProject, getProjectByName, getProjectComponents, getProjectComponentsByName, getAllProjects, getAllComponents]);

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
